import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { X } from 'lucide-react';
import API_BASE_URL from '../config/api';

const ActionCoachButton = () => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const { getToken } = useAuth();

  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  // Get today's date in YYYY-MM-DD format (local timezone)
  const getToday = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  // Format AI response text to HTML
  const formatResponse = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900">$1</strong>')
      .replace(/\n\n/g, '</p><p class="mb-3">')
      .replace(/\n---\n/g, '</p><hr class="my-4 border-gray-200"/><p class="mb-3">')
      .replace(/\n(\d+)\.\s*Action:/g, '</p><p class="mb-2 mt-4 font-semibold text-[#2FA163]">$1. Action:')
      .replace(/\n\s*How to do it:/g, '</p><p class="mb-2 ml-4 text-gray-700"><span class="font-medium">How to do it:</span>')
      .replace(/\n\s*Why this matters:/g, '</p><p class="mb-2 ml-4 text-gray-600 text-sm"><span class="font-medium">Why this matters:</span>')
      .replace(/\nConstraint:/g, '</p><p class="mb-2 font-semibold text-red-600">Constraint:')
      .replace(/\nFocus:/g, '</p><p class="mb-3 font-semibold text-blue-600">Focus:')
      .replace(/^/, '<p class="mb-3 text-lg font-medium text-[#2FA163]">')
      .replace(/$/, '</p>');
  };

  const generateCoaching = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const token = await getToken();
      const currentMonth = getCurrentMonth();
      const today = getToday();

      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Gather all context data in parallel
      const [trackerSettings, dailyTracker, pnlData, activityLogs, goalSettings] = await Promise.all([
        fetch(`${API_BASE_URL}/api/tracker/settings?month=${currentMonth}`, {
          headers,
          credentials: 'include'
        }).then(r => r.ok ? r.json() : null).catch(() => null),

        fetch(`${API_BASE_URL}/api/tracker/daily?date=${today}`, {
          headers,
          credentials: 'include'
        }).then(r => r.ok ? r.json() : null).catch(() => null),

        fetch(`${API_BASE_URL}/api/pnl/summary?month=${currentMonth}`, {
          headers,
          credentials: 'include'
        }).then(r => r.ok ? r.json() : null).catch(() => null),

        fetch(`${API_BASE_URL}/api/activity-logs?limit=10`, {
          headers,
          credentials: 'include'
        }).then(r => r.ok ? r.json() : null).catch(() => null),

        fetch(`${API_BASE_URL}/api/goal-settings`, {
          headers,
          credentials: 'include'
        }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);

      // Call AI coach with action_coaching context
      const aiResponse = await fetch(`${API_BASE_URL}/api/ai-coach/generate`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          context: 'action_coaching',
          tracker_settings: trackerSettings,
          daily_tracker: dailyTracker,
          pnl_data: pnlData,
          recent_activity_logs: activityLogs,
          goal_settings: goalSettings,
        }),
      });

      if (!aiResponse.ok) {
        throw new Error('AI response failed');
      }

      const data = await aiResponse.json();
      // Get the response text from various possible fields
      const responseText = data.response || data.message || data.coaching || 
                          data.formatted_analysis || data.summary || 
                          (typeof data === 'string' ? data : JSON.stringify(data));
      setResponse(responseText);
    } catch (err) {
      console.error('Action coach error:', err);
      setError('Unable to generate coaching. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-trigger coaching when modal opens
  useEffect(() => {
    if (showModal && !response && !loading) {
      generateCoaching();
    }
  }, [showModal]);

  const handleFeedback = async (type) => {
    try {
      const token = await getToken();
      await fetch(`${API_BASE_URL}/api/activity-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'ai_coach_feedback',
          feedback: type,
          date: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  const handleDone = () => {
    handleFeedback('done');
    setShowModal(false);
    setResponse(null);
  };

  const handleDifferent = () => {
    handleFeedback('different');
    generateCoaching();
  };

  return (
    <>
      {/* Compact Button - matches Refresh Fairy AI Coach style */}
      <div className="flex justify-center my-4">
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-300 text-sm"
          data-testid="action-coach-button"
        >
          <span>✨</span>
          <span>What should I do right now?</span>
        </button>
      </div>

      {/* Modal Popup */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4" 
          style={{ zIndex: 9999 }}
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Header - Green gradient */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b bg-gradient-to-r from-[#2FA163] to-[#268a54] text-white rounded-t-lg flex-shrink-0">
              <div className="flex items-center">
                <span className="text-xl mr-2">✨</span>
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold">Fairy AI Coach</h2>
                  <p className="text-green-100 text-xs sm:text-sm">Action Coaching</p>
                </div>
              </div>
              <button 
                onClick={() => { setShowModal(false); setResponse(null); }}
                className="text-white hover:text-green-200 transition-colors p-1"
                data-testid="action-coach-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div 
              className="flex-1 overflow-y-auto overscroll-contain" 
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="p-4 sm:p-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2FA163]"></div>
                    <p className="mt-4 text-gray-500">Analyzing your pipeline...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-red-500 mb-4">{error}</p>
                    <button 
                      onClick={generateCoaching}
                      className="px-4 py-2 bg-[#2FA163] hover:bg-[#268a54] text-white rounded-lg transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                ) : response ? (
                  <div className="prose prose-sm max-w-none">
                    <div 
                      dangerouslySetInnerHTML={{ __html: formatResponse(response) }} 
                      className="text-gray-700 leading-relaxed"
                    />
                  </div>
                ) : null}
              </div>
            </div>

            {/* Footer with feedback buttons */}
            {response && !loading && (
              <div className="border-t p-4 flex gap-3 flex-shrink-0 bg-gray-50 rounded-b-lg">
                <button
                  onClick={handleDone}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium text-sm transition-colors border border-green-200"
                  data-testid="action-coach-done"
                >
                  <span>✅</span><span>I did this</span>
                </button>
                <button
                  onClick={handleDifferent}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-medium text-sm transition-colors border border-blue-200"
                  data-testid="action-coach-different"
                >
                  <span>🔁</span><span>Different actions</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ActionCoachButton;
